import { useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { setBookingStep } from '@/store/booking/booking.slice';
import { useRouter } from 'next/router';
import { pathsOrder } from '@/config/estepperConfig';

export default function useStepper() {
  const paths = useSelector((state) => state.paths, shallowEqual);

  const router = useRouter();

  const dispatch = useDispatch();

  const getFlow = () =>
    pathsOrder
      .map((path) => {
        return paths[path];
      })
      .filter((path) => path.available);

  // useMemo keeps stepperFlow synchronized with paths within the same
  // render cycle. The previous useState + useEffect approach introduced a
  // one-render gap where stepperFlow (and stepperFlowRef) were stale after
  // paths changed, causing navigation functions to use an outdated flow.
  const stepperFlow = useMemo(() => getFlow(), [paths]);

  const breadcrumbFlow = useMemo(() => {
    const flow = getFlow();
    return flow.slice(1, flow.length - 1);
  }, [paths]);

  // Keep a ref to the latest stepperFlow so that navigation functions
  // captured in stale closures (e.g., setTimeout callbacks) always read
  // the current value instead of the one from the render when they were
  // created.
  const stepperFlowRef = useRef(stepperFlow);
  stepperFlowRef.current = stepperFlow;

  const getStep = (index) => stepperFlowRef.current[index];

  const currentStep = () => {
    const currentPath =
      router.pathname === '/'
        ? 'HOME'
        : router.pathname.replace('/', '').toUpperCase();

    let currentIndex = stepperFlowRef.current.findIndex(
      (step) => currentPath === step.name,
    );

    return { currentIndex, currentPath };
  };

  const getPreviousStep = (stepIndex) => {
    const { currentIndex } = currentStep();
    return getStep(currentIndex - 1);
  };

  const changeStep = (stepIndex) => {
    const { path, name } = getStep(stepIndex);
    router.push(path);
    if (name !== 'HOME' && name !== 'SUCCESS') dispatch(setBookingStep(name));
  };

  // When the current step is not in stepperFlow (currentIndex === -1),
  // it means the step was disabled at runtime (e.g., ADDONS disabled
  // because the ticket-insurance service went down while the user was
  // already on that page). In that case, find the nearest available step
  // in pathsOrder instead of falling back to index 0 (HOME).
  const findNextAvailableFrom = (stepName) => {
    const orderIndex = pathsOrder.indexOf(stepName);
    if (orderIndex === -1) return -1;

    for (let i = orderIndex + 1; i < pathsOrder.length; i++) {
      const flowIndex = stepperFlowRef.current.findIndex(
        (step) => step.name === pathsOrder[i],
      );
      if (flowIndex >= 0) return flowIndex;
    }
    return -1;
  };

  const findPrevAvailableFrom = (stepName) => {
    const orderIndex = pathsOrder.indexOf(stepName);
    if (orderIndex === -1) return -1;

    for (let i = orderIndex - 1; i >= 0; i--) {
      const flowIndex = stepperFlowRef.current.findIndex(
        (step) => step.name === pathsOrder[i],
      );
      if (flowIndex >= 0) return flowIndex;
    }
    return -1;
  };

  const nextStep = () => {
    const { currentIndex, currentPath } = currentStep();
    if (currentPath === 'SUCCESS') return;

    if (currentIndex === -1) {
      const fallbackIndex = findNextAvailableFrom(currentPath);
      if (fallbackIndex >= 0) {
        changeStep(fallbackIndex);
      }
      return;
    }

    changeStep(currentIndex + 1);
  };

  const prevStep = () => {
    const { currentIndex, currentPath } = currentStep();
    if (currentPath === 'HOME') return;

    if (currentIndex === -1) {
      const fallbackIndex = findPrevAvailableFrom(currentPath);
      if (fallbackIndex >= 0) {
        changeStep(fallbackIndex);
      }
      return;
    }

    changeStep(currentIndex - 1);
  };

  // PATH in uppercase pe: PAYMENT
  const jumpTo = (path, query) => {
    const index = stepperFlowRef.current.findIndex((step) => step.name === path);
    if (index >= 0) {
      changeStep(index);
      return;
    }

    if (paths[path]) {
      router.push(`${paths[path].path}${query ? query : ''}`);
      if (path !== 'HOME' && path !== 'SUCCESS') {
        dispatch(setBookingStep(path));
      }
    }
  };

  const success = async () => {
    if (router.pathname === '/success') {
      dispatch(setBookingStep(paths.SUCCESS.name));
    }
  };

  return {
    stepperFlow,
    breadcrumbFlow,
    nextStep,
    prevStep,
    currentStep,
    getPreviousStep,
    jumpTo,
    success,
  };
}
